import { compact, groupBy, map } from "lodash";

import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { formatCurrencyAmount, formatPercentage } from "lib/format";
import { AssetAdministration } from "services/assetAdministrations/types";
import { InvestmentTerm } from "services/listings/types";

export type BuyerDashboardItem = {
  activeRelistRequestId: number | null;
  assetIds: number[];
  currency: string;
  dealTitle: string;
  dealUrl: string | null;
  hasPaymentStatusUpdate: boolean;
  incompletePartialResale: boolean;
  isHeld: boolean;
  lastUpdate: string;
  listingIds: number[];
  priorPaymentAmount: string | null;
  previousHubspotDealId: string | null;
  purchasedDate: string;
  purchasedPrice: string;
  purchasedTerm: InvestmentTerm;
  resaleInfoId: number;
  returnOnInvestmentAmount: string;
  returnOnInvestmentPercentage: string;
  royaltiesReceived: string;
  saleDate: string | null;
  saleProceeds: string;
  totalYield: string;
};

export const getBuyerDashboardItems = (
  assetAdministrations: AssetAdministration[],
) => {
  // Group asset administrations by resale_info.id
  const groupedByResaleInfo = groupBy(
    assetAdministrations,
    assetAdministration => assetAdministration.asset.resale_info.id,
  );

  const buyerDashboardItems = map(
    groupedByResaleInfo,
    (assetAdministrations, resaleInfoId) => {
      // All asset administrations in this group share the same resale_info
      const resaleInfo = assetAdministrations[0].asset.resale_info;

      const purchasedPrice = resaleInfo.purchased_amount
        ? (
            Number(resaleInfo.purchased_amount) + Number(resaleInfo.buyer_fee)
          ).toString()
        : "";

      // get sale dates from related assets, remove `nulls`, take the latest sale date
      const saleDate = compact(
        assetAdministrations.map(
          assetAdministration => assetAdministration.asset.sale_date,
        ),
      )
        .sort()
        .pop();

      // NOTE: make sure to match the field's type for empty values to keep nulls-last sorting
      return {
        activeRelistRequestId: resaleInfo.active_relist_request_id ?? null,
        assetIds: assetAdministrations.map(aa => aa.asset.id),
        currency: resaleInfo.currency ?? "",
        dealTitle: resaleInfo.deal_title ?? null,
        dealUrl: resaleInfo.deal_url ?? null,
        hasPaymentStatusUpdate: !!compact(
          assetAdministrations.map(
            assetAdministration => assetAdministration.payment_status,
          ),
        ).length,
        incompletePartialResale: resaleInfo.incomplete_partial_resale ?? false,
        isHeld: resaleInfo.incomplete_partial_resale || !saleDate,
        lastUpdate: resaleInfo.last_update ?? "",
        listingIds: resaleInfo.listings,
        priorPaymentAmount: resaleInfo.prior_payment_amount ?? null,
        previousHubspotDealId: resaleInfo.previous_hubspot_deal_id ?? null,
        purchasedDate: resaleInfo.purchased_date ?? "",
        purchasedPrice,
        purchasedTerm: resaleInfo.purchased_term ?? null,
        resaleInfoId: Number(resaleInfoId),
        returnOnInvestmentAmount:
          resaleInfo.return_on_investment_amount ?? null,
        returnOnInvestmentPercentage:
          resaleInfo.return_on_investment_percentage ?? null,
        royaltiesReceived: resaleInfo.royalties_received ?? null,
        saleDate: saleDate ?? null,
        saleProceeds: resaleInfo.sale_proceeds ?? null,
        totalYield: resaleInfo.total_yield ?? null,
      };
    },
  );
  return buyerDashboardItems;
};

export const getStats = (items: BuyerDashboardItem[]) => {
  const heldItems = items.filter(item => item.isHeld);
  const soldItems = items.filter(item => !item.isHeld);
  return {
    averageROI: averageROI(soldItems),
    averageYield: averageYield(heldItems),
    heldCount: heldItems.length,
    soldCount: soldItems.length,
    totalReturn: totalReturn(soldItems),
    totalRoyaltiesReceived: totalRoyaltiesReceived(items),
    totalSaleProceeds: totalSaleProceeds(soldItems),
  };
};

const totalRoyaltiesReceived = (items: BuyerDashboardItem[]) => {
  const totalRoyaltiesReceived = items.reduce((sum, item) => {
    return sum + Math.round(parseFloat(item.royaltiesReceived || "0"));
  }, 0);

  return formatCurrencyAmount("USD", totalRoyaltiesReceived, 0);
};

const averageYield = (heldItems: BuyerDashboardItem[]) => {
  if (!heldItems.length) {
    return EMPTY_VALUE_PLACEHOLDER;
  }
  const totalYield = heldItems.reduce((sum, item) => {
    const itemYield = !!item.totalYield ? parseFloat(item.totalYield) : 0;
    return sum + itemYield;
  }, 0);

  return formatPercentage(totalYield / heldItems.length);
};

const totalSaleProceeds = (soldItems: BuyerDashboardItem[]) => {
  if (!soldItems.length) {
    return EMPTY_VALUE_PLACEHOLDER;
  }
  const totalSaleProceeds = soldItems.reduce((sum, item) => {
    return sum + (Math.round(parseFloat(item.saleProceeds)) || 0);
  }, 0);

  return formatCurrencyAmount("USD", totalSaleProceeds, 0);
};

const averageROI = (soldItems: BuyerDashboardItem[]) => {
  if (!soldItems.length) {
    return EMPTY_VALUE_PLACEHOLDER;
  }
  const totalROI = soldItems.reduce((sum, item) => {
    const assetROI = !!item.returnOnInvestmentPercentage
      ? parseFloat(item.returnOnInvestmentPercentage)
      : 0;
    return sum + assetROI;
  }, 0);

  return formatPercentage(totalROI / soldItems.length);
};

const totalReturn = (soldItems: BuyerDashboardItem[]) => {
  if (!soldItems.length) {
    return EMPTY_VALUE_PLACEHOLDER;
  }
  const totalReturn = soldItems.reduce((sum, item) => {
    const itemROIAmount = !!item.returnOnInvestmentAmount
      ? Math.round(parseFloat(item.returnOnInvestmentAmount))
      : 0;
    return sum + itemROIAmount;
  }, 0);

  return formatCurrencyAmount("USD", totalReturn, 0);
};
